import { Fragment, ReactNode, useEffect, useRef, useState } from 'react';
import NavigationLink from 'components/Navigation/NavigationLink';
import LanguageSelector from 'components/LanguageSelector';
import { useTranslation } from 'react-i18next';
import { URLS } from 'config/urls';

interface MobileNavigationProps {
  openIcon: ReactNode;
  closeIcon: ReactNode;
  handleLogout: () => Promise<void>;
}

const MENU_PORTAL_ID = 'headlessui-portal-root';

const MobileNavigation = ({ openIcon, closeIcon, handleLogout }: MobileNavigationProps) => {
  const [mounted, setMounted] = useState(false);
  const [animate, setAnimate] = useState(false);
  const ref = useRef<HTMLElement>(null);
  const { t } = useTranslation();

  const openMenu = () => {
    setMounted(true);

    requestAnimationFrame(() => {
      setAnimate(true);
    });
  };

  const closeMenu = () => {
    setAnimate(false);
    setTimeout(() => setMounted(false), 300); // match duration-300
  };

  useEffect(() => {
    const handleOutsideMenu = (event: MouseEvent) => {
      const langEl = document.getElementById(MENU_PORTAL_ID);
      if (ref.current && !ref.current.contains(event.target as Node) && !langEl?.contains(event.target as Node)) {
        closeMenu();
      }
    };

    const handleEscapeKey = (event: KeyboardEvent) => {
      const langEl = document.getElementById(MENU_PORTAL_ID);
      if (event.key === 'Escape' && langEl === null) closeMenu();
    };

    document.addEventListener('keyup', handleEscapeKey);
    document.addEventListener('click', handleOutsideMenu, true);

    return () => {
      document.removeEventListener('keyup', handleEscapeKey);
      document.removeEventListener('click', handleOutsideMenu, true);
    };
  }, []);

  return (
    <Fragment>
      <div className='rounded-full bg-primary50 absolute top-3 right-6 md:hidden'>
        <button className='relative p-2' onClick={openMenu}>
          {openIcon}
        </button>
      </div>

      {mounted && (
        <Fragment>
          <div
            className={`
              fixed inset-0 bg-grey800 transition-opacity duration-300
              ${animate ? 'opacity-50' : 'opacity-0'}
            `}
          />

          <nav
            ref={ref}
            className={`
              fixed top-0 right-0 w-2/3 h-screen bg-white shadow-2xs
              transition-transform duration-300 ease-out
              ${animate ? 'translate-x-0' : 'translate-x-full'}
            `}
          >
            <header className='w-full h-[65px] bg-beige relative'>
              <div className='rounded-full bg-primary50 absolute shadow-2xs top-3 left-6'>
                <button className='relative p-2' onClick={closeMenu}>
                  {closeIcon}
                </button>
              </div>
            </header>

            <div className='flex flex-col items-center gap-4 h-screen pt-10'>
              <NavigationLink href={URLS.FLIGHTS}>{t('navigation.flights')}</NavigationLink>
              <button
                onClick={handleLogout}
                className='text-grey500 font-[MuseoSans900] uppercase tracking-[1.5px] cursor-pointer'
              >
                {t('navigation.logout')}
              </button>

              <div className='bg-beige w-full flex justify-center'>
                <LanguageSelector />
              </div>
            </div>
          </nav>
        </Fragment>
      )}
    </Fragment>
  );
};

export default MobileNavigation;
